// FoxError.jsx
import "./IvError.css";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Modal, Table, Button } from "react-bootstrap";
import { getIvDataIndex, getIvFieldValue } from "../../utils/ivErrorDataUtils";

// 40075 Inverter State enum 맵
const INVERTER_STATE_MAP = {
  0: "OFF",
  1: "SLEEPING",
  2: "STARTING",
  3: "MPPT",
  4: "THROTTLED",
  5: "SHUTTING_DOWN",
  6: "FAULT",
  7: "STANDBY",
};

// 40075 상태 모달용 “비트맵” 데이터 (값별 목록)
const FoxStateModalData = {
  title: "40075 Inverter State",
  rows: [
    { value: 0, label: "OFF" },
    { value: 1, label: "SLEEPING" },
    { value: 2, label: "STARTING" },
    { value: 3, label: "MPPT" },
    { value: 4, label: "THROTTLED" },
    { value: 5, label: "SHUTTING_DOWN" },
    { value: 6, label: "FAULT" },
    { value: 7, label: "STANDBY" },
  ],
};

// 40077 Alarm Bitfield 비트맵 (U32)
const FoxAlarmModalData = {
  title: "40077 Alarm Bitfield (Active alarms for the DER)",
  rows: [
    { bit: "Bit0", desc: "Ground Fault" },
    { bit: "Bit1", desc: "DC Over Voltage" },
    { bit: "Bit2", desc: "AC Disconnect Open" },
    { bit: "Bit3", desc: "DC Disconnect Open" },
    { bit: "Bit4", desc: "Grid Disconnect" },
    { bit: "Bit5", desc: "Cabinet Open" },
    { bit: "Bit6", desc: "Manual Shutdown" },
    { bit: "Bit7", desc: "Over Temperature" },
    { bit: "Bit8", desc: "Frequency Above Limit" },
    { bit: "Bit9", desc: "Frequency Under Limit" },
    { bit: "Bit10", desc: "AC Voltage Above Limit" },
    { bit: "Bit11", desc: "AC Voltage Under Limit" },
    { bit: "Bit12", desc: "Blown String Fuse on Input" },
    { bit: "Bit13", desc: "Under Temperature" },
    {
      bit: "Bit14",
      desc: "Generic Memory or Communication Error (Internal)",
    },
    { bit: "Bit15", desc: "Hardware Test Failure" },
    { bit: "Bit16", desc: "Manufacturer Alarm" }
  ],
};

const parseBitFlags32 = (value) => {
  const flags = {};
  if (typeof value !== "number") return flags;
  for (let i = 0; i < 32; i++) {
    flags[`Bit${i}`] = (value & (1 << i)) !== 0;
  }
  return flags;
};

const isStateAbnormal = (stateValue) => {
  if (typeof stateValue !== "number") return false;
  // 예시: MPPT(3) / STANDBY(7)만 정상 취급
  return !(stateValue === 3 || stateValue === 7);
};

const isAlarmAbnormal = (alarmValue) => {
  if (typeof alarmValue !== "number") return false;
  return alarmValue !== 0;
};

const FoxError = ({ SelInverter }) => {
  const ivData = useSelector((state) => state.ivData || {});
  const [showStateModal, setShowStateModal] = useState(false);
  const [showAlarmModal, setShowAlarmModal] = useState(false);
  const dataIndex = getIvDataIndex(ivData, SelInverter);

  const getFieldValue = (key) => {
    return getIvFieldValue(ivData, key, SelInverter);
  };

  const inverterState = getFieldValue("state"); // 40075
  const alarmField = getFieldValue("fault0");   // 40077 U32
  const alarmBits = parseBitFlags32(alarmField);

  const renderStateModal = () => {
    const modal = FoxStateModalData;
    return (
      <Modal
        show={showStateModal}
        onHide={() => setShowStateModal(false)}
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered size="sm">
            <thead>
              <tr>
                <th>Value</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {modal.rows.map((row, idx) => {
                const isActive = row.value === inverterState;
                return (
                  <tr key={idx} className={isActive ? "active-row" : ""}>
                    <td>{row.value}</td>
                    <td>{row.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStateModal(false)}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderAlarmModal = () => {
    const modal = FoxAlarmModalData;
    return (
      <Modal
        show={showAlarmModal}
        onHide={() => setShowAlarmModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered size="sm">
            <tbody>
              {modal.rows.map((row, idx) => {
                const isActive = alarmBits[row.bit];
                return (
                  <tr key={idx} className={isActive ? "active-row" : ""}>
                    <td>{row.bit}</td>
                    <td>{row.desc}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAlarmModal(false)}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  if (dataIndex === -1) {
    return <div className="status-list-container">인버터를 선택하세요.</div>;
  }

  const stateText =
    typeof inverterState === "number"
      ? INVERTER_STATE_MAP[inverterState] ?? `Unknown(${inverterState})`
      : "-";

  return (
    <div className="status-list-container">
      <div className="status-list">
        {/* 40075 Inverter State */}
        <div className="status-item">
          <span className="status-value">
            40075 Inverter State: {stateText} ({inverterState ?? "-"})
          </span>
          <button onClick={() => setShowStateModal(true)}>
            <span className={isStateAbnormal(inverterState) ? "blinking-icon" : ""}>
              {isStateAbnormal(inverterState) ? "🔴" : "🟢"}
            </span>
          </button>
        </div>

        {/* 40077 Alarm Bitfield */}
        <div className="status-item">
          <span className="status-value">
            40077 Alarm Bitfield: {alarmField ?? 0} (
            0x
            {typeof alarmField === "number"
              ? alarmField.toString(16).toUpperCase()
              : "0"}
            )
          </span>
          <button onClick={() => setShowAlarmModal(true)}>
            <span className={isAlarmAbnormal(alarmField) ? "blinking-icon" : ""}>
              {isAlarmAbnormal(alarmField) ? "🔴" : "🟢"}
            </span>
          </button>
        </div>
      </div>

      {showStateModal && renderStateModal()}
      {showAlarmModal && renderAlarmModal()}
    </div>
  );
};

export default FoxError;
